import React, { useCallback, useEffect, useRef, useState } from "react";

import { ArrowRight, Ellipsis, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";

import PageErrorState from "../../../common/PageErrorState/PageErrorState";
import SmallCFLoader from "../../../common/SmallCFLoader/SmallCFLoader";
import Toast from "../../../common/Toast/Toast";
import EntityConstants from "../../../configs/EntityConstants";
import { cn } from "../../../lib/utils";
import { GET, POST } from "../../../service/CRUDService";
import { automationTestIds } from "../../../testing/automationTestIds";

export const OTHER_CATEGORY_ID = "__other__";
export const OTHER_CATEGORY_VALUE = "others";
export const GOAL_CATEGORY_STORAGE_KEY = "onboarding_goal_category";

const readStoredGoalCategory = () => {
  try {
    return sessionStorage.getItem(GOAL_CATEGORY_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const writeStoredGoalCategory = (id) => {
  try {
    if (id) {
      sessionStorage.setItem(GOAL_CATEGORY_STORAGE_KEY, id);
    } else {
      sessionStorage.removeItem(GOAL_CATEGORY_STORAGE_KEY);
    }
  } catch {
    // Private mode can refuse storage; the step still works without it.
  }
};

export const buildCategoryPayload = ({ selectedIds, otherSelected }) => {
  if (otherSelected) {
    return [OTHER_CATEGORY_VALUE];
  }
  return Array.from(new Set((selectedIds || []).filter(Boolean)));
};

const SELECTED_TINT =
  "bg-[color-mix(in_srgb,var(--cf-primary-color)_6%,white)]";
const HOVER_BORDER =
  "hover:border-[color-mix(in_srgb,var(--cf-primary-color)_45%,white)]";
const TILE_IDLE =
  "bg-[color-mix(in_srgb,var(--cf-primary-color)_7%,white)] border-[color-mix(in_srgb,var(--cf-primary-color)_12%,white)]";
const TILE_SELECTED =
  "bg-[color-mix(in_srgb,var(--cf-primary-color)_16%,white)] border-[color-mix(in_srgb,var(--cf-primary-color)_40%,white)]";
const SELECTED_EDGE = "shadow-[0_0_0_1px_var(--cf-primary-color)]";

const CategoryCard = ({
  category,
  selected,
  onToggle,
  disabled,
  Icon = Sparkles,
}) => {
  const thumbnail = Array.isArray(category?.thumbnail_images)
    ? category.thumbnail_images[0]
    : null;
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const node = imageRef.current;
    if (node && node.complete && node.naturalWidth > 0) {
      setImageLoaded(true);
    }
  }, [thumbnail]);

  const showImage = Boolean(thumbnail) && !imageFailed;

  return (
    <button
      type="button"
      role="radio"
      id={`onboarding-category-card-${category.category_id}`}
      aria-checked={selected}
      disabled={disabled}
      onMouseDown={(event) => event.preventDefault()}
      onClick={() => onToggle(category.category_id)}
      style={{ outline: "none", WebkitTapHighlightColor: "transparent" }}
      className={cn(
        "group relative flex w-full select-none items-start gap-4 rounded-xl border-[1px] p-4 text-left",
        "transition-[border-color,background-color,box-shadow,transform] duration-200 ease-out",
        "active:scale-[0.99] focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2",
        selected
          ? cn("border-cfPrimary", SELECTED_TINT, SELECTED_EDGE)
          : cn(
              "border-cfCardBorder bg-cfSecondary hover:shadow-sm",
              HOVER_BORDER,
            ),
        disabled && "cursor-not-allowed opacity-60 active:scale-100",
      )}
    >
      <span
        className={cn(
          "flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border-[1px] transition-colors duration-200",
          selected ? TILE_SELECTED : TILE_IDLE,
        )}
      >
        {showImage ? (
          <img
            ref={imageRef}
            src={thumbnail}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageFailed(true)}
            className={cn(
              "h-9 w-9 object-contain transition-opacity duration-300",
              imageLoaded ? "opacity-100" : "opacity-0",
            )}
          />
        ) : (
          <Icon
            className="h-5 w-5 text-cfPrimary"
            strokeWidth={1.75}
            aria-hidden="true"
          />
        )}
      </span>

      <span className="min-w-0 flex-1 pr-7">
        <span className="block text-[15px] font-semibold leading-snug text-cfHeadingText">
          {category.category_name}
        </span>
        {category.category_description ? (
          <span className="mt-1 block text-sm leading-snug text-cfGlobalText line-clamp-3">
            {category.category_description}
          </span>
        ) : null}
      </span>

      <span
        aria-hidden="true"
        className={cn(
          "absolute right-4 top-4 flex h-5 w-5 items-center justify-center rounded-full border-[1px] transition-[background-color,border-color] duration-200",
          selected
            ? "border-cfPrimaryButton"
            : "border-cfSignInSignUpInputBorder bg-cfSecondary group-hover:border-cfPrimary",
        )}
      >
        <span
          className={cn(
            "h-2.5 w-2.5 rounded-full bg-cfPrimaryButton transition-all duration-200",
            selected ? "scale-100 opacity-100" : "scale-50 opacity-0",
          )}
        />
      </span>
    </button>
  );
};

const SKELETON_SLOTS = Array.from(
  { length: 6 },
  (_, slot) => `skeleton-category-${slot}`,
);

const CategorySkeleton = () => {
  const { t } = useTranslation(["onboarding"]);

  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
    >
      {SKELETON_SLOTS.map((slot) => (
        <div
          key={slot}
          className="flex items-start gap-4 rounded-xl border-[1px] border-cfCardBorder bg-cfSecondary p-4"
        >
          <div className="h-11 w-11 shrink-0 animate-pulse rounded-xl bg-gray-200" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-2/3 animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-3 w-4/5 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      ))}
      <span className="sr-only">{t("onboarding:loading")}</span>
    </div>
  );
};

const CategorySelectionStep = ({ onComplete }) => {
  const { t } = useTranslation(["onboarding"]);

  const [categories, setCategories] = useState([]);
  const [selectedId, setSelectedId] = useState(readStoredGoalCategory);
  const [isLoading, setIsLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadCategories = useCallback(async () => {
    setIsLoading(true);
    setLoadFailed(false);

    try {
      const response = await GET(
        `${EntityConstants.DISCOVERY_DEFAULT_CATEGORY}?limit=50&offset=0`,
        { skipAuth: true },
      );
      if (!response || !response.ok) {
        setLoadFailed(true);
        return;
      }
      const data = await response.json();
      setCategories(Array.isArray(data?.results) ? data.results : []);
    } catch (error) {
      console.error(error);
      setLoadFailed(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const selectCategory = useCallback((categoryId) => {
    setSelectedId((current) => {
      const next = current === categoryId ? null : categoryId;
      writeStoredGoalCategory(next);
      return next;
    });
  }, []);

  const otherSelected = selectedId === OTHER_CATEGORY_ID;
  const selectedIds = otherSelected || !selectedId ? [] : [selectedId];

  const handleContinue = async () => {
    if (isSaving) {
      return;
    }

    if (!selectedId) {
      onComplete();
      return;
    }

    const payload = buildCategoryPayload({
      selectedIds,
      otherSelected,
    });

    setIsSaving(true);
    let saved = false;
    try {
      const response = await POST(EntityConstants.POST_USER_CATEGORIES, {
        category_id: payload,
      });
      saved = Boolean(response?.ok);
    } catch (error) {
      console.error(error);
    }
    setIsSaving(false);

    if (saved) {
      onComplete();
      return;
    }
    Toast({
      type: "error",
      title: t("onboarding:goal_save_error_title"),
      message: t("onboarding:goal_save_error_message"),
    });
  };

  const hasCategories = categories.length > 0;
  const hasSelection = Boolean(selectedId);
  const canContinue = hasCategories ? hasSelection : true;

  return (
    <div className="flex flex-col gap-8">
      {isLoading ? <CategorySkeleton /> : null}

      {!isLoading && loadFailed ? (
        <div id={automationTestIds.onboardingCategoryError}>
          <PageErrorState
            variant="section"
            title={t("onboarding:goal_load_error_title")}
            description={t("onboarding:goal_load_error_description")}
            primaryLabel={t("onboarding:goal_retry")}
            onPrimary={loadCategories}
          />
        </div>
      ) : null}

      {!isLoading && !loadFailed && !hasCategories ? (
        <div className="rounded-xl border-[1px] border-dashed border-cfCardBorder bg-cfSecondary p-8 text-center">
          <p className="text-[15px] font-semibold text-cfHeadingText">
            {t("onboarding:goal_empty_title")}
          </p>
          <p className="mt-1 text-sm text-cfGlobalText">
            {t("onboarding:goal_empty_description")}
          </p>
        </div>
      ) : null}

      {!isLoading && !loadFailed && hasCategories ? (
        <div
          id={automationTestIds.onboardingCategoryGrid}
          role="radiogroup"
          aria-label={t("onboarding:goal_title")}
          className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
        >
          {categories.map((category) => (
            <CategoryCard
              key={category.category_id}
              category={category}
              selected={selectedId === category.category_id}
              onToggle={selectCategory}
              disabled={isSaving}
            />
          ))}
          <CategoryCard
            category={{
              category_id: OTHER_CATEGORY_ID,
              category_name: t("onboarding:goal_other_title"),
              category_description: t("onboarding:goal_other_description"),
            }}
            Icon={Ellipsis}
            selected={otherSelected}
            onToggle={selectCategory}
            disabled={isSaving}
          />
        </div>
      ) : null}

      {!isLoading && !loadFailed ? (
        <div className="flex flex-col gap-3 border-t border-cfCardBorder pt-6 sm:flex-row sm:items-center sm:justify-end">
          {hasCategories && !hasSelection ? (
            <p className="text-sm text-cfGlobalText sm:mr-auto">
              {t("onboarding:goal_select_one")}
            </p>
          ) : null}
          <button
            type="button"
            id={automationTestIds.onboardingCategoryContinue}
            onClick={handleContinue}
            disabled={!canContinue || isSaving}
            className="inline-flex min-w-[9rem] items-center justify-center gap-2 whitespace-nowrap rounded-[8px] bg-cfPrimaryButton px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-cfPrimaryDark focus:outline-none focus-visible:ring-2 focus-visible:ring-cfPrimaryButton focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-cfDisabled"
          >
            {isSaving ? (
              <SmallCFLoader text={t("onboarding:goal_continue")} />
            ) : (
              <>
                {t("onboarding:goal_continue")}
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </>
            )}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default CategorySelectionStep;
